// «Что нового»: значок в полосе разделов, три окна-панели и строка «Программа X · История
// обновлений».
//
// ## Три окна, и это не дубли
//
// | окно | когда открывается | откуда текст |
// |---|---|---|
// | «Что нового в X» (`update:X`) | вышла версия X, у человека ещё старая | манифест или App Store |
// | «Что нового в X» (`whats-new:X`) | версия X только что встала, один раз сама | история в сборке |
// | «История обновлений» (`versions:all`) | человек сам захотел посмотреть | история в сборке |
//
// У первых двух одинаковый заголовок, а содержимое разное по происхождению: до установки текста
// новой версии в сборке нет и быть не может, после установки он уже внутри и сеть не нужна.
// Рисует их один `СписокИзменений`, чтобы «новое» и «исправлено» выглядели одинаково везде.
//
// ## Панели, а не окна поверх всего
//
// `ПРАВИЛА.md`, правило 4: модальное окно — только для необратимого подтверждения. Список
// изменений читают, не бросая работу, и закрывают так же, как любую панель: крестиком, Esc,
// нажатием мимо. Адрес панели настоящий — `?p=versions:all` можно переслать.
//
// ## Когда «Что нового» открывается само
//
// Один раз после установки новой версии (`useРассказОбОбновлении`, его зовёт каркас защищённой
// области). Финансовый директор 14.10.2026 написал «пока не было обновлений» о версии, которая у
// него уже стояла: программа обновилась молча. Окно засчитывается фактом показа — закрыть его
// не читая человек вправе, и тот же список через день был бы шумом.

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { RiDownloadCloud2Line } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';
import { create } from 'zustand';

import { registerPanel } from '@/layout/panel-registry';
import {
  открытьМагазин,
  сравнитьВерсии,
  установитьОбновление,
  useОбновление,
} from '@/shared/desktop/обновление';
import {
  ВЕРСИЯ_ПРОГРАММЫ,
  группыЗаписи,
  ждётРассказа,
  историяДо,
  отметитьРассказ,
  показРазрешён,
  разобратьЗаметки,
  чтоНовогоВ,
  type ГруппаИзменений,
  type ЗаписьВерсии,
} from '@/shared/desktop/что-нового';
import { buildSearch, parseSearch, pushPanel } from '@/shared/routing/search';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import * as LinkButton from '@/shared/ui/link-button';
import { useСреда } from '@/shared/среда';

/** Вид панели в адресе: новая версия до установки, `?p=update:1.0.25`. */
export const PANEL_UPDATE = 'update';
/** Вид панели в адресе: что изменилось в только что вставшей версии, `?p=whats-new:1.0.24`. */
export const PANEL_WHATS_NEW = 'whats-new';
/** Вид панели в адресе: история установленных версий, `?p=versions:all`. */
export const PANEL_VERSIONS = 'versions';
const ИСТОРИЯ_ВСЯ = 'all';

/** Открыть панель поверх текущего экрана, не уводя человека из раздела. */
export function useОткрытьПанель(): (вид: string, id: string, заменить?: boolean) => void {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (вид: string, id: string, заменить = false) => {
      const состояние = pushPanel(parseSearch(location.search), { view: вид, id });
      navigate(`${location.pathname}${buildSearch(состояние)}`, { replace: заменить });
    },
    [location.pathname, location.search, navigate],
  );
}

/** Открыть историю обновлений. Одно место на «Настройки» и на окно своего имени. */
export function useОткрытьИсторию(): () => void {
  const открыть = useОткрытьПанель();
  return useCallback(() => открыть(PANEL_VERSIONS, ИСТОРИЯ_ВСЯ), [открыть]);
}

// ── Список изменений ───────────────────────────────────────────────────────────────────────

/** Группы «Новое» и «Исправлено» с пунктами. Один на все три окна. */
export function СписокИзменений({ группы }: { группы: readonly ГруппаИзменений[] }) {
  return (
    <div className='flex flex-col gap-3'>
      {группы.map((группа, номер) => (
        <section
          key={`${группа.заголовок ?? 'без-заголовка'}-${номер}`}
          className='flex flex-col gap-1.5'
        >
          {группа.заголовок !== null && (
            <h4 className='text-label-xs text-text-sub-600'>{группа.заголовок}</h4>
          )}
          <ul className='ml-4 flex list-disc flex-col gap-1 text-paragraph-sm text-text-strong-950'>
            {группа.пункты.map((пункт, место) => (
              <li key={`${место}-${пункт}`}>{пункт}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** Одна версия: номер, день выхода и что в ней изменилось. */
function ВерсияИИзменения({
  запись,
  сНомером,
  установлена = false,
}: {
  запись: ЗаписьВерсии;
  /** Номер не пишется, когда он уже стоит в заголовке панели. */
  сНомером: boolean;
  установлена?: boolean;
}) {
  return (
    <article className='flex flex-col gap-2'>
      {сНомером ? (
        <header className='flex flex-wrap items-center gap-x-2 gap-y-1'>
          <h3 className='text-label-md text-text-strong-950'>Версия {запись.версия}</h3>
          {установлена && (
            <Badge.Root variant='lighter' color='blue' size='medium'>
              у вас
            </Badge.Root>
          )}
          <span className='text-paragraph-xs text-text-sub-600'>{formatDateOnly(запись.дата)}</span>
        </header>
      ) : (
        <p className='text-paragraph-xs text-text-sub-600'>Вышла {formatDateOnly(запись.дата)}</p>
      )}
      <СписокИзменений группы={группыЗаписи(запись)} />
    </article>
  );
}

/** Несколько версий подряд, от новой к старой, с чертой между ними. */
function СписокВерсий({
  записи,
  установлена,
}: {
  записи: readonly ЗаписьВерсии[];
  установлена?: string;
}) {
  return (
    <div className='flex flex-col divide-y divide-stroke-soft-200'>
      {записи.map((запись) => (
        <div key={запись.версия} className='py-4 first:pt-0 last:pb-0'>
          <ВерсияИИзменения
            запись={запись}
            сНомером
            установлена={установлена !== undefined && запись.версия === установлена}
          />
        </div>
      ))}
    </div>
  );
}

// ── Окно «Что нового» перед обновлением ────────────────────────────────────────────────────

/**
 * Открыто ли окно «Что нового» перед обновлением. Пока открыто, полоса внизу окна молчит.
 *
 * У окна своя кнопка «Обновить», у полосы — своя, и вдвоём на одном экране это два одинаковых
 * действия рядом: человек ищет между ними разницу, которой нет (сквозная проверка панелей
 * краснеет на «кнопка названа дважды»). Окно закрыли — полоса вернулась, если её не откладывали.
 */
export const useОкноОбновленияОткрыто = create<{ открыто: boolean }>(() => ({ открыто: false }));

/**
 * Новая версия, которая ещё не стоит: что в ней и кнопка, которая её ставит.
 *
 * Кнопка та же, что у полосы внизу окна, и делает то же самое. Это одно действие в двух местах
 * намеренно: полоса — для того, кто уже решил, окно — для того, кто сначала хочет прочитать.
 * На планшете ставит магазин, поэтому кнопка ведёт туда, а не ставит сама.
 */
export function ОкноОбновления({ версия }: { версия: string }) {
  const этап = useОбновление((состояние) => состояние.этап);
  const предложено = useОбновление((состояние) => состояние.версия);
  const заметки = useОбновление((состояние) => состояние.заметки);
  const группы = useMemo(() => разобратьЗаметки(заметки), [заметки]);
  const открытьИсторию = useОткрытьИсторию();

  useEffect(() => {
    useОкноОбновленияОткрыто.setState({ открыто: true });
    return () => useОкноОбновленияОткрыто.setState({ открыто: false });
  }, []);

  // Окно открыли ссылкой, а версия уже не та, что ждёт установки: обновились, или программа
  // ещё не успела спросить сайт после запуска. Кнопка здесь ставила бы не то, что написано.
  if (этап === 'нет' || предложено !== версия) {
    const ужеСтоит = сравнитьВерсии(версия, ВЕРСИЯ_ПРОГРАММЫ) <= 0;
    return (
      <div className='flex flex-col items-start gap-3'>
        <p className='text-paragraph-sm text-text-sub-600'>
          {ужеСтоит
            ? `Версия ${версия} у вас уже стоит. Что в ней изменилось, написано в истории обновлений.`
            : `Версия ${версия} ещё не готова к установке. Программа проверяет обновления сама и покажет значок «Есть обновление», когда всё будет готово.`}
        </p>
        <LinkButton.Root
          type='button'
          variant='primary'
          size='medium'
          underline
          className='цель-тача relative'
          onClick={открытьИсторию}
        >
          История обновлений
        </LinkButton.Root>
      </div>
    );
  }

  const вМагазине = этап === 'в-магазине';
  const ставится = этап === 'ставится';

  return (
    <div className='flex flex-col gap-4'>
      {группы.length > 0 ? (
        <СписокИзменений группы={группы} />
      ) : (
        // Манифест или магазин ничего не сказали. Пустое окно читалось бы как поломка, а
        // правда в том, что список уже лежит внутри новой версии и покажется после установки.
        <p className='text-paragraph-sm text-text-sub-600'>
          Список изменений программа покажет сразу после обновления.
        </p>
      )}
      <div className='flex flex-col gap-2'>
        <Button.Root
          type='button'
          variant='primary'
          mode='filled'
          size='medium'
          className='цель-тача relative w-full'
          disabled={ставится}
          onClick={вМагазине ? открытьМагазин : () => void установитьОбновление()}
        >
          {вМагазине ? 'Открыть в App Store' : ставится ? 'Устанавливаю…' : 'Обновить'}
        </Button.Root>
        <p className='text-paragraph-xs text-text-sub-600'>
          {вМагазине
            ? 'Обновление ставит App Store, это меньше минуты: ваши данные на сервере и останутся на месте.'
            : 'Обновление займёт около полуминуты, программа закроется и откроется снова.'}
        </p>
      </div>
    </div>
  );
}

// ── Окно «Что нового» после установки ──────────────────────────────────────────────────────

/**
 * Что изменилось в версии, которая только что встала.
 *
 * Сразу после установки рассказывает обо всём пропущенном: обновился с 1.0.22 сразу на 1.0.24 —
 * увидит обе. Открытое позже ссылкой — об одной версии X.
 */
export function ОкноЧтоНового({ версия }: { версия: string }) {
  const записи = useMemo(() => чтоНовогоВ(версия), [версия]);
  const открытьИсторию = useОткрытьИсторию();

  // Засчитывается фактом показа, а не нажатием «понятно»: закрыть, не читая, человек вправе,
  // и любой учёт «по закрытию» потерял бы этот случай (так же устроены подсказки).
  useEffect(() => {
    отметитьРассказ(версия);
  }, [версия]);

  return (
    <div className='flex flex-col gap-4'>
      {записи.length === 0 ? (
        <p className='text-paragraph-sm text-text-sub-600'>
          Список изменений этой версии не записан.
        </p>
      ) : записи.length === 1 && записи[0] !== undefined ? (
        <ВерсияИИзменения запись={записи[0]} сНомером={false} />
      ) : (
        <СписокВерсий записи={записи} />
      )}
      <LinkButton.Root
        type='button'
        variant='primary'
        size='medium'
        underline
        className='цель-тача relative self-start'
        onClick={открытьИсторию}
      >
        Вся история обновлений
      </LinkButton.Root>
    </div>
  );
}

// ── История обновлений ─────────────────────────────────────────────────────────────────────

/** Все вышедшие версии до установленной, от новой к старой. */
export function ОкноИстории() {
  const записи = useMemo(() => историяДо(), []);
  // Свою версию человек находит по метке «у вас» на её записи. Отдельная строка «У вас версия X»
  // над ней повторяла бы то же самое вторыми словами — она нужна, только когда записи о своей
  // версии в истории нет.
  const своюВидно = записи.some((запись) => запись.версия === ВЕРСИЯ_ПРОГРАММЫ);
  return (
    <div className='flex flex-col gap-4'>
      {!своюВидно && (
        <p className='text-paragraph-sm text-text-sub-600'>У вас версия {ВЕРСИЯ_ПРОГРАММЫ}.</p>
      )}
      {записи.length === 0 ? (
        <p className='text-paragraph-sm text-text-sub-600'>
          Список изменений появится со следующего обновления.
        </p>
      ) : (
        <СписокВерсий записи={записи} установлена={ВЕРСИЯ_ПРОГРАММЫ} />
      )}
    </div>
  );
}

// ── Строка версии и значок ─────────────────────────────────────────────────────────────────

/**
 * «Программа 1.0.24 · История обновлений» — внизу «Настроек» и в окне своего имени.
 *
 * Два места, а не одно, по той же причине, что смена фразы в окне имени: «Настройки» открыты не
 * всем ролям, а своё имя внизу полосы разделов видит каждый. Строка одна на оба места: номер и
 * подпись не разъедутся.
 */
export function СтрокаВерсии({ открыть }: { открыть: () => void }) {
  return (
    <p className='text-paragraph-xs text-text-sub-600'>
      Программа {ВЕРСИЯ_ПРОГРАММЫ} ·{' '}
      <button
        type='button'
        onClick={открыть}
        className='цель-тача relative underline decoration-dotted underline-offset-2 transition duration-200 ease-out hover:text-text-strong-950'
      >
        История обновлений
      </button>
    </p>
  );
}

/**
 * «Есть обновление» в нижней группе полосы разделов.
 *
 * Полоса внизу окна уходит по «Позже» на два часа (на планшете на сутки), и до 1.0.24 вместе с ней
 * пропадала и сама новость: человек, отложивший обновление, не мог вернуться к нему иначе, как
 * дождавшись полосы. Значок остаётся, пока версия ждёт установки, и ведёт к списку изменений —
 * «заметная иконка», о которой просил финансовый директор.
 *
 * Цвет основной, а не красный: красный в продукте занят деньгами и отказом.
 *
 * Надпись — `text-feature-dark`, а не `text-primary-*`. Тон тот же фиолетовый, но у основного нет
 * своего цвета для тёмной темы: на бледной подложке `primary-base` даёт 4,18:1 в светлой и 3,61:1
 * в тёмной, `primary-dark` — 8,60:1 и 1,76:1. `feature-dark` в тёмной теме светлеет сам:
 * 11,77:1 и 4,79:1, под курсором 10,81:1 и 4,52:1 (порог 4,5:1, держит `ЧтоНового.test.tsx`).
 */
export function ЗначокОбновления() {
  const этап = useОбновление((состояние) => состояние.этап);
  const версия = useОбновление((состояние) => состояние.версия);
  const открыть = useОткрытьПанель();
  const среда = useСреда();

  if (этап === 'нет' || версия === null) {
    return null;
  }
  // На узкой полосе слов помещается два коротких, как у разделов над ним. Выбор кодом, а не
  // прятанием: спрятанный вариант остался бы в разметке вторым именем той же кнопки.
  const узкая = среда.ширина !== 'широко';

  return (
    <div className='border-t border-stroke-soft-200 px-3 py-2 тесно:px-1'>
      <button
        type='button'
        onClick={() => открыть(PANEL_UPDATE, версия)}
        title={`Что нового в версии ${версия}`}
        className='цель-тача relative flex w-full items-center gap-2.5 rounded-10 bg-primary-alpha-10 px-3 py-2 text-left text-feature-dark transition duration-200 ease-out hover:bg-primary-alpha-16 тесно:min-h-14 тесно:flex-col тесно:gap-1 тесно:px-1 тесно:py-1.5 тесно:text-center'
      >
        <RiDownloadCloud2Line className='size-5 shrink-0' />
        {узкая ? (
          <span className='text-paragraph-xs leading-tight'>Новая версия</span>
        ) : (
          <span className='flex min-w-0 flex-col'>
            <span className='text-label-sm'>Есть обновление</span>
            <span className='text-paragraph-xs'>Версия {версия}: что нового</span>
          </span>
        )}
      </button>
    </div>
  );
}

// ── Рассказ после установки ────────────────────────────────────────────────────────────────

/**
 * Один раз после установки открыть «Что нового в X».
 *
 * Зовётся каркасом защищённой области: окно нужно тому, кто уже внутри, а не экрану входа. Если
 * после обновления сессия протухла, рассказ дождётся входа — память о нём не тронута, пока окно
 * не нарисовано. На стартовом адресе «/» ждёт перехода на раздел: тот адрес сам себя заменяет, и
 * открытая на нём панель пропала бы вместе с ним.
 */
export function useРассказОбОбновлении(): void {
  const location = useLocation();
  const открыть = useОткрытьПанель();
  const спрошено = useRef(false);

  useEffect(() => {
    if (спрошено.current || location.pathname === '/') {
      return;
    }
    спрошено.current = true;
    if (!показРазрешён()) {
      return;
    }
    const ждёт = ждётРассказа();
    if (ждёт !== null) {
      // Заменой, а не новой записью: «назад» после закрытия не должен открывать его снова.
      открыть(PANEL_WHATS_NEW, ждёт.до, true);
    }
  }, [location.pathname, открыть]);
}

registerPanel(PANEL_UPDATE, {
  title: (версия) => `Что нового в ${версия}`,
  render: ({ id }) => <ОкноОбновления версия={id} />,
});

registerPanel(PANEL_WHATS_NEW, {
  title: (версия) => `Что нового в ${версия}`,
  render: ({ id }) => <ОкноЧтоНового версия={id} />,
});

registerPanel(PANEL_VERSIONS, {
  title: () => 'История обновлений',
  render: () => <ОкноИстории />,
});
